// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// API pozivi
import { getResidualValueAPI } from "services/api/calculations/residualvalue/api";

// Tipovi i konstante
import { ResidualValueDetailFormValues } from "services/api/calculations/residualvalue/types";
import { Errors } from "services/forms/validation/types";
import {
  ResidualValueDetailPageCmp,
  ResidualValueDetailPageProps,
} from "./types";
import { initialResidualValueDetailForm } from "services/api/calculations/residualvalue/consts";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Komponente specifične za stranicu
import ResidualValueDetailCard from "./components/ResidualValueDetailCard/ResidualValueDetailCard";
import ResidualValueDeleteCard from "./components/ResidualValueDeleteCard/ResidualValueDeleteCard";

// Pomoćni moduli
import { transformRoute, useTranslate } from "helpers";
import { Routes } from "ui/modules/calculation/navigation";

// Konstante
import { CALCULATION_RESIDUAL_VALUE_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_RESIDUAL_VALUE } from "app/components/Sidebar/consts";

const ResidualValueDetailPage: ResidualValueDetailPageCmp = (
  props: ResidualValueDetailPageProps
) => {
  const { residualValueId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<ResidualValueDetailFormValues>(
    initialResidualValueDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_RESIDUAL_VALUE_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (residualValueId) fetchResidualValue(residualValueId);
  }, [residualValueId]);

  const fetchResidualValue = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialResidualValueDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getResidualValueAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_RESIDUAL_VALUE.text,
      route: SIDEBAR_ITEM_CALCULATION_RESIDUAL_VALUE.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationResidualValueSearchPageRouter, {
        residualValueId,
      })
    );
  }, []);

  if (!residualValueId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${title.text}`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t(
                    "admin.calculation.setting.residualvalue.detail.delete"
                  )}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <ResidualValueDetailCard residualValueId={residualValueId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.residualvalue.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <ResidualValueDeleteCard
              residualValueId={residualValueId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

ResidualValueDetailPage.route = Routes.CalculationResidualValueDetailPageRouter;

export default ResidualValueDetailPage;
